import { useState } from 'react';
import { createAccount, login } from '../../api/accounts';
import { ApiError } from '../../api/client';
import { Card } from '../../components/Card';
import { useToasts } from '../../hooks/useToasts';
import { humanise } from '../../lib/labels';
import { toCents } from '../../lib/money';

interface Props {
  hidden: boolean;
  onSignIn: (accountId: string) => void;
  onAccountCreated: () => Promise<void>;
}

/**
 * A basic username/password gate. This is not meant to withstand a real
 * attacker - there is no session, just enough friction that the demo is not
 * wide open to whoever finds the URL.
 */
export function LoginScreen({ hidden, onSignIn, onAccountCreated }: Props) {
  const { toast } = useToasts();
  const [mode, setMode] = useState<'signIn' | 'signUp'>('signIn');

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [balance, setBalance] = useState('250');
  const [busy, setBusy] = useState(false);

  async function signIn() {
    if (!name.trim() || !password) return toast('Enter a name and password.', 'warn');
    setBusy(true);
    try {
      const account = await login(name.trim(), password);
      onSignIn(account.id);
    } catch (err) {
      toast(humanise(err instanceof ApiError ? err.code : undefined), 'bad');
    } finally {
      setBusy(false);
    }
  }

  async function signUp() {
    const trimmed = name.trim();
    const dollars = Number(balance);
    if (!trimmed || !password) return toast('Enter a name and password.', 'warn');
    if (!Number.isFinite(dollars) || dollars < 0) {
      return toast('Opening balance must be zero or more.', 'warn');
    }

    setBusy(true);
    try {
      const account = await createAccount(trimmed, password, toCents(dollars));
      await onAccountCreated();
      onSignIn(account.id);
    } catch (err) {
      toast(humanise(err instanceof ApiError ? err.code : undefined), 'bad');
    } finally {
      setBusy(false);
    }
  }

  function submit() {
    void (mode === 'signIn' ? signIn() : signUp());
  }

  const submitLabel = mode === 'signIn' ? 'Sign in' : 'Create account';

  return (
    <section id="login" className={`login-wrap${hidden ? ' hidden' : ''}`}>
      <Card>
        <h1>{mode === 'signIn' ? 'Sign in' : 'Create an account'}</h1>
        <p className="muted small" style={{ margin: '6px 0 16px' }}>
          A basic username and password - just enough to keep this demo from
          being wide open.
        </p>

        <label className="field">
          <span>Name</span>
          <input
            id="login-name"
            value={name}
            placeholder="Dana"
            autoComplete="username"
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <label className="field">
          <span>Password</span>
          <input
            id="login-password"
            type="password"
            value={password}
            autoComplete={mode === 'signIn' ? 'current-password' : 'new-password'}
            onChange={(event) => setPassword(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && submit()}
          />
        </label>

        {mode === 'signUp' && (
          <label className="field">
            <span>Opening balance (dollars)</span>
            <input
              id="new-balance"
              type="number"
              min="0"
              step="0.01"
              value={balance}
              onChange={(event) => setBalance(event.target.value)}
            />
          </label>
        )}

        <button id="login-submit" className="primary wide" disabled={busy} onClick={submit}>
          {busy ? 'Working…' : submitLabel}
        </button>

        <button
          id="login-toggle-mode"
          className="ghost small wide"
          style={{ marginTop: 8 }}
          onClick={() => setMode(mode === 'signIn' ? 'signUp' : 'signIn')}
        >
          {mode === 'signIn' ? 'Create an account instead' : 'Sign in instead'}
        </button>
      </Card>
    </section>
  );
}
